import { createAsyncThunk } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import normalizeError from '@rsos/sinatra/src/utils/normalizeError';
import {
  mapLayerSettingsSelector,
  StateWithPsaps,
} from '../capstone/psapInfo/psapInfoSlice';
import { getLoadedGISServices, loadSingleLayer } from './gisServicesAPI';
import {
  FETCH_GIS_SERVICES,
  LOAD_GIS_LAYER,
  type FetchGISServicesArg,
  type FetchGISServicesPayload,
  type FetchGISServicesRejection,
  type LoadGISLayerArg,
  type LoadGISLayerPayload,
  type LoadGISLayerRejection,
} from './gisServicesActions';
import { type GISServicesState } from './gisServicesTypes';
import { computeFilteredSublayerIds } from './roleFiltering';

/**
 * The ArcGIS-bound half of the gisServices feature. Kept out of
 * gisServicesSlice so importing the reducers or selectors does not drag
 * @arcgis/core into apps that never load a layer.
 */
export const fetchGISServices = createAsyncThunk<
  FetchGISServicesPayload,
  FetchGISServicesArg,
  {
    state: StateWithPsaps & {
      gisServices: GISServicesState;
      sinatra?: {
        user?: {
          currentRole?: {
            name?: string;
            application?: string;
            permissions?: string[];
          };
        };
      };
    };
    rejectValue: FetchGISServicesRejection;
  }
>(
  FETCH_GIS_SERVICES,
  async ({ orgName, shouldLoadLayers }, { rejectWithValue, getState }) => {
    try {
      const settings = mapLayerSettingsSelector(getState());
      const state = getState();
      const roleName = state.sinatra?.user?.currentRole?.name;
      const currentPSAP = state.psaps?.currentPSAP;
      const accountId = currentPSAP?.account_id;

      const result = await getLoadedGISServices(
        orgName,
        settings,
        shouldLoadLayers,
      );

      return {
        ...result,
        roleName,
        accountId,
      };
    } catch (error) {
      const { message } = normalizeError(error);
      return rejectWithValue({
        message,
        status: (error as AxiosError).response?.status,
      });
    }
  },
);

export const loadGISLayer = createAsyncThunk<
  LoadGISLayerPayload,
  LoadGISLayerArg,
  {
    state: { gisServices: GISServicesState };
    rejectValue: LoadGISLayerRejection;
  }
>(LOAD_GIS_LAYER, async ({ service }, { rejectWithValue, getState }) => {
  try {
    const state = getState();
    const roleName = state.gisServices.roleName;
    const loadedLayer = await loadSingleLayer(service);

    return {
      layerId: service.gisLayerID,
      loadedLayer: {
        ...loadedLayer,
        filteredSublayerIds: computeFilteredSublayerIds(loadedLayer, roleName),
      },
    };
  } catch (error) {
    return rejectWithValue({
      message: error instanceof Error ? error.message : 'Failed to load layer',
      layerId: service.gisLayerID,
    });
  }
});
